import React, { useState, useRef } from 'react';
import Dropdown from '../Common/Dropdown';
import IconPicker from './IconPicker';
import { CourseIcon, ICONS } from './iconLibrary';
import { ShellCover, Popover, EXAMS, hueToHex, hueOfHex } from './CourseSettings';

/* Окно «Новый курс» — тот же каркас (cs2-*), что и «Настройки курса»:
   превью с фоном-узором, название, тип экзамена, состояние, цвет-ползунок и иконки.
   Дополнительно — копирование структуры существующего курса. */
const STATE = [{ v: true, l: 'Черновик' }, { v: false, l: 'Опубликован' }];

const CourseCreate = ({ value, onChange, onSubmit, onClose, courses = [], creating }) => {
  const [showPalette, setShowPalette] = useState(false);
  const [allIcons, setAllIcons] = useState(false);
  const hueRef = useRef(null);
  const swatchRef = useRef(null);
  const allIconsRef = useRef(null);

  const set = (patch) => onChange(v => ({ ...v, ...patch }));
  const isCopy = !!value.copyFrom;

  const setHueFromX = (clientX) => {
    const el = hueRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    set({ color: hueToHex(Math.max(0, Math.min(1, (clientX - r.left) / r.width)) * 360) });
  };
  const onHueDown = (e) => {
    e.preventDefault(); setHueFromX(e.clientX);
    const move = (ev) => setHueFromX(ev.clientX);
    const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
  };

  const suggested = ICONS.slice(0, 11);
  const segCls = (active) => `cs2-seg${active ? ' on' : ''}`;
  const huePos = `${(hueOfHex(value.color) / 360) * 100}%`;
  const draft = isCopy ? true : value.draft;

  return (
    <div className="cs2-card">
      {/* ── Шапка ── */}
      <div className="cs2-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="cs2-title">{isCopy ? 'Копия курса' : 'Новый курс'}</div>
          <div className="cs2-sub">{isCopy ? 'Структура скопируется, курс создастся черновиком' : 'Название, обложка и состояние нового курса'}</div>
        </div>
        <button className="cs2-save" onClick={onSubmit} disabled={creating}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {creating ? 'Создание…' : (isCopy ? 'Скопировать' : 'Создать курс')}
        </button>
        <button className="cs2-x" onClick={onClose} aria-label="Закрыть">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      {/* ── Тело ── */}
      <div className="cs2-body">

        {/* Превью будущей карточки курса */}
        <div className="cs2-preview">
          <ShellCover id="new" />
          <div className="cs2-ava"><CourseIcon icon={value.icon} size={34} sw={1.8} /></div>
          <div className="cs2-pv-info">
            <div className="cs2-pv-name">{value.name || 'Название курса'}</div>
            <div className="cs2-pv-badges">
              <span className="cs2-pv-pill"><span style={{ width: 7, height: 7, borderRadius: '50%', background: value.color }} />{value.examType || 'Курс'}</span>
              <span className="cs2-pv-pill" style={draft ? { background: 'rgba(245,158,11,0.16)', color: '#d97706' } : { background: 'rgba(34,197,94,0.16)', color: '#16a34a' }}>
                {draft ? 'Черновик' : 'Опубликован'}
              </span>
            </div>
          </div>
        </div>

        {/* Название */}
        <div className="cs2-field">
          <label className="cs2-lbl">Название курса</label>
          <input className="cs2-input" value={value.name} autoFocus
            onChange={e => set({ name: e.target.value })} placeholder="Напр. ЕГЭ информатика 2026" />
        </div>

        {/* Копия структуры существующего курса */}
        <div className="cs2-field">
          <label className="cs2-lbl">Скопировать структуру из</label>
          <Dropdown
            value={value.copyFrom} onChange={(v) => set({ copyFrom: v })} placeholder="Не копировать (пустой курс)"
            options={[{ value: '', label: 'Не копировать (пустой курс)' }, ...courses.map(c => ({ value: String(c.id), label: c.name }))]}
          />
          {isCopy && (
            <div className="cs2-rule-s" style={{ marginTop: 7, lineHeight: 1.45 }}>
              Скопируются разделы, вебинары, ДЗ и варианты. Ответы и прогресс учеников — нет.
            </div>
          )}
        </div>

        {!isCopy && (
          <>
            <div className="cs2-hr" />

            {/* Тип экзамена + состояние */}
            <div className="cs2-grid2">
              <div>
                <label className="cs2-lbl">Тип экзамена</label>
                <div className="cs2-segrow">
                  {EXAMS.map(e => (
                    <button key={e.v || 'none'} type="button" className={segCls(value.examType === e.v)} onClick={() => set({ examType: e.v })}>{e.l}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="cs2-lbl">Состояние</label>
                <div className="cs2-segrow">
                  {STATE.map(s => (
                    <button key={String(s.v)} type="button" className={segCls(value.draft === s.v)} onClick={() => set({ draft: s.v })}>{s.l}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Цвет — свотч + hex + ползунок тона */}
            <div className="cs2-field">
              <label className="cs2-lbl">Цвет плашки <span className="dim">— {value.color}</span></label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button ref={swatchRef} type="button" className="cs2-swatch" style={{ background: value.color }}
                  onClick={() => setShowPalette(p => !p)} title="Подобрать оттенок" aria-label="Подобрать оттенок" />
                <input className="cs2-hex" value={value.color}
                  onChange={e => { let v = e.target.value.trim(); if (v && !v.startsWith('#')) v = '#' + v; set({ color: v }); }} />
              </div>
              <Popover anchorRef={swatchRef} open={showPalette} onClose={() => setShowPalette(false)} width={248}>
                <div className="cs2-hue" ref={hueRef} onMouseDown={onHueDown} title="Подобрать оттенок">
                  <span className="cs2-hue-knob" style={{ left: huePos, background: value.color }} />
                </div>
              </Popover>
            </div>

            {/* Иконка */}
            <div className="cs2-field">
              <label className="cs2-lbl">Иконка</label>
              <div className="cs2-iconrow">
                {suggested.map(ic => (
                  <button key={ic.key} type="button" title={ic.name} className={`cs2-iconcell${value.icon === ic.key ? ' on' : ''}`} onClick={() => set({ icon: ic.key })}>
                    <CourseIcon icon={ic.key} size={22} sw={1.7} />
                  </button>
                ))}
                <button ref={allIconsRef} type="button" className="cs2-allicons" onClick={() => setAllIcons(a => !a)}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>
                  Все иконки
                </button>
              </div>
              <Popover anchorRef={allIconsRef} open={allIcons} onClose={() => setAllIcons(false)} width={300}>
                <IconPicker value={value.icon} onChange={ic => set({ icon: ic })} />
              </Popover>
            </div>
          </>
        )}

        <div style={{ height: 8 }} />
      </div>

      {/* ── Футер ── */}
      <div className="cs2-foot">
        <span className="cs2-foot-note">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          Обложку и настройки можно поменять позже
        </span>
        <button className="cs2-close" onClick={onClose}>Отмена</button>
      </div>
    </div>
  );
};

export default CourseCreate;
